import { useEffect, useState } from 'react'
import { getCountriesApi, type Country } from '@/lib/api'

export interface CountriesResult {
  countries: Country[]
  loading: boolean
}

/** sessionStorage key for the static country seed list. */
const STORAGE_KEY = 'peerpass_countries'

/**
 * Load the full country list for the pricing country-override dropdown.
 *
 * The list is static seed data (196 rows, incl. inline flag data URIs), so it
 * is cached per tab — repeat visits render the dropdown instantly and we never
 * re-download the flags. Failures degrade to an empty list (the dropdown shows
 * just the "Auto-detect" option until a retry on the next page load).
 */
export function useCountries(): CountriesResult {
  const [countries, setCountries] = useState<Country[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as Country[]
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCountries(parsed)
          setLoading(false)
          return
        }
      }
    } catch {
      // Corrupt cache — refetch.
    }

    async function load() {
      try {
        const { data } = await getCountriesApi()
        if (cancelled) return
        setCountries(data)
        if (data.length > 0) {
          try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
          } catch {
            // Storage full/blocked — the in-memory list still works.
          }
        }
      } catch {
        if (!cancelled) setCountries([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { countries, loading }
}
